import React from 'react';
import { substituteTokens, substituteSubject } from './merge';
import PropertyPicker from './PropertyPicker';

// Drawn rather than typed: ‹ and › are hairlines in Jost and read as 4px of
// ink in a 26px button, however large the font-size is set.
const Chevron = ({ direction }) => (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor"
        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={direction === 'left' ? 'M8 3 L4.5 6.5 L8 10' : 'M5 3 L8.5 6.5 L5 10'}/>
    </svg>
);

/**
 * The email as one recipient will read it, with a way to step through them —
 * an owner with four properties and an owner with one look different.
 */
export default function Preview({
    title = 'Preview', className = '', recipient, index, total, subject, bodyHtml, sender, options,
    choosable, onChooseProperty, onStep, onRemove, showPropertyCount = true,
}) {
    const settings = options || {};

    const html = React.useMemo(
        () => (recipient ? substituteTokens(bodyHtml, recipient, sender, settings) : ''),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [recipient, bodyHtml, sender, settings.choice, settings.overrides, settings.listings, settings.listingOverrides],
    );

    const subjectLine = React.useMemo(
        () => (recipient ? substituteSubject(subject, recipient, sender, settings) : ''),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [recipient, subject, sender, settings.choice, settings.overrides, settings.listings, settings.listingOverrides],
    );

    const properties = (recipient && recipient.properties) || [];

    return (
        <div className={'mm-card mm-preview' + (className ? ' ' + className : '')}>
            <div className="mm-preview__head">
                <div className="mm-panel__title">{title}</div>
                <div className="mm-spacer"/>
                {recipient ? (
                    <React.Fragment>
                        {onRemove ? <button className="mm-removebtn" onClick={onRemove}>Remove</button> : null}
                        <button className="mm-stepbtn" aria-label="Previous recipient" onClick={() => onStep(-1)}>
                            <Chevron direction="left"/>
                        </button>
                        <div className="mm-preview__step">{index + 1} of {total}</div>
                        <button className="mm-stepbtn" aria-label="Next recipient" onClick={() => onStep(1)}>
                            <Chevron direction="right"/>
                        </button>
                    </React.Fragment>
                ) : null}
            </div>

            {recipient ? (
                <React.Fragment>
                    <div className="mm-preview__who">
                        {recipient.name}
                        {/* A vendor or agent letter is about nobody's property. */}
                        {showPropertyCount ? (
                            <span className="mm-pill">
                                {recipient.property_count} {recipient.property_count === 1 ? 'property' : 'properties'}
                            </span>
                        ) : null}
                    </div>

                    {/* Which of their properties this letter is about. Only worth
                        asking when they own more than one. */}
                    {choosable && properties.length > 1 ? (
                        <div className="mm-preview__pick">
                            <span className="mm-preview__picklabel">Write to them about</span>
                            <PropertyPicker
                                properties={properties}
                                value={settings.choice}
                                onChange={(propertyId) => onChooseProperty(recipient.id, propertyId)}
                                ariaLabel={'Property this letter names for ' + recipient.name}
                            />
                        </div>
                    ) : null}

                    <div className="mm-preview__body">
                        <div className="mm-mail">
                            <div className="mm-mail__head">
                                {/* The mailbox, not the team record's address: the message
                                    leaves whichever Outlook account was connected. */}
                                <div>From <span>{sender.name} &lt;{sender.mailbox || sender.email}&gt;</span></div>
                                <div>To <span>{recipient.name} &lt;{recipient.email}&gt;</span></div>
                                <div className="mm-mail__subject">{subjectLine}</div>
                            </div>
                            <div className="mm-mail__content" dangerouslySetInnerHTML={{ __html: html }}/>
                        </div>
                    </div>
                </React.Fragment>
            ) : (
                <div className="mm-empty">No recipients have an email on file — nothing to preview.</div>
            )}
        </div>
    );
}
